// Constantes de gameplay partagées par le moteur et l'interface.
// Unités « monde » : la fenêtre du viseur mesure VIEW_SIZE × VIEW_SIZE unités quelle que soit
// la taille CSS du canvas (le renderer applique l'échelle). Les valeurs viennent du prototype.

export const VIEW_SIZE = 320;        // largeur de la fenêtre du viseur, en unités monde
// Taille (diamètre, unités monde) du portrait d'un fantôme dans le viseur : effet de perspective,
// petit en haut de la fenêtre (loin), grand en bas (proche) — voir drawGhosts dans ui/renderer.js.
export const GHOST_MIN_SIZE = 30;
export const GHOST_MAX_SIZE = 110;
// Kami déjà dans l'album (#20) : il reste sur la carte, estompé (opacité du portrait, sans halo) et marqué d'une coche, pour
// qu'on ne le prenne pas pour un kami à photographier (il ne se photographie plus).
export const KNOWN_GHOST_ALPHA = 0.45;
export const GRID_STEP = 80;         // pas de la grille phosphorescente de la carte
// Aucun fantôme n'APPARAÎT à moins de cette distance du bord du monde (voir generateGhosts dans
// core/world.js) : à VIEW_SIZE / 2 du bord au minimum (voir getPanLimit), sinon il pourrait naître
// hors de portée de l'objectif. +20 de confort pour rester capturable même avec un petit rayon de
// capture. En dérive, il va ensuite jusqu'au bord du décor (voir GHOST_EDGE_TURN_BAND).
export const WORLD_MARGIN = VIEW_SIZE / 2 + 20;
// Dérive près du ciel/sol (bords du décor en Y ; l'axe X boucle, sans bord) : dans cette bande
// (unités monde) avant le bord, un kami qui s'en approche vire en courbe — rayon de virage égal à
// cette largeur — au lieu de rebondir, et son portrait touche tout juste le bord dans le pire cas
// (arrivée perpendiculaire).
export const GHOST_EDGE_TURN_BAND = 120;
// Un kami ne monte ni ne descend jamais à moins de cette distance du bord du décor en Y : la moitié de la hauteur du viseur
// (ce que l'objectif peut atteindre, voir getPanLimit) plus un cercle du réticule (diamètre = 2 × r=36 dans index.html). Il
// vire donc un peu avant la butée de l'objectif, au lieu d'aller jusqu'au bord pour qu'on s'y arrête pour le centrer.
export const RETICLE_DIAMETER = 72;
export const GHOST_EDGE_MARGIN = VIEW_SIZE / 2 + RETICLE_DIAMETER;
export const CENTER_CLEARANCE = 150; // aucun fantôme cadré au départ (l'objectif démarre au centre)

// Glisser sur le viseur (voir ui/input.js) : le décor suit le doigt, puis garde son élan au
// lâcher. La vitesse du lâcher est mesurée sur les derniers DRAG_VELOCITY_WINDOW_MS du geste ; un
// doigt resté immobile plus de DRAG_STILL_MS avant de se lever ne lance rien. L'élan est plafonné
// (DRAG_FLING_MAX_SPEED, unités monde/s) puis freiné (dragFriction, réglage dev) jusqu'à
// DRAG_INERTIA_MIN_SPEED.
export const DRAG_VELOCITY_WINDOW_MS = 100;
export const DRAG_STILL_MS = 60;
export const DRAG_FLING_MAX_SPEED = 2400;
export const DRAG_INERTIA_MIN_SPEED = 8;

// Visée au gyroscope (voir core/orientation.js#gyroPanDelta) : un tour complet sur soi parcourt
// toute la largeur du monde (× gyroYawSpeed, réglage dev) ; viser GYRO_BASE_PITCH_RANGE degrés (÷ gyroPitchSpeed)
// au-dessus ou au-dessous de l'horizon mène au bord du monde en hauteur. Le gyroscope ne compte comme une « commande » (qui éteint
// l'aide des coins) qu'après GYRO_INPUT_DEG de mouvement : le tremblement de la main n'y suffit pas.
// Sans aucune mesure GYRO_LIVE_TIMEOUT_MS après l'activation, l'appareil n'a pas de capteur.
export const GYRO_BASE_PITCH_RANGE = 60;
export const GYRO_INPUT_DEG = 3;
// Téléphone baissé jusqu'à cette inclinaison (degrés, 0 = horizon, < 0 = vers le sol) : le gyroscope se coupe et on revient
// au mode normal (glisser)
export const GYRO_LOWERED_PITCH = -25;
export const GYRO_LIVE_TIMEOUT_MS = 1500;

// Niveau N = N kami de chaque type de la famille (voir data/family.js et core/level.js), dans un
// monde qui s'agrandit d'autant ; au niveau 1, un monde de 2000 × 2000, agrandi proportionnellement
// pour garder la même densité qu'au temps de la famille à 9.
export const BASE_WORLD_SIZE = 2000;
export const BASE_MIN_SPACING = 320; // distance minimale entre deux fantômes au niveau 1
export const SPACING_GROWTH = 30;    // … et gagnée à chaque niveau (fantômes plus espacés)
export const MAX_LEVEL = 6;
// Batterie longue durée (gagnée en partageant tous les kamis, voir core/shareRewards.js) : termine la charge tout de suite, et la batterie
// dure LONG_LIFE_FACTOR fois plus longtemps (autonomie ×, photos moins coûteuses) jusqu'à la prochaine batterie vide
export const LONG_LIFE_FACTOR = 2;
// Kamis dans la famille à chaque niveau (un par niveau, du niveau 1 au dernier) : 5, 7, 9, 11, 13 puis les 15 au niveau 6
export const LEVEL_FAMILY_SIZES = Object.freeze([5, 7, 9, 11, 13, 15]);
// Visiteur (pas de compte) : seulement ce nombre de kamis dans la famille, à tous les niveaux ; les autres demandent de se connecter

export const GALLERY_LOCAL_LIMIT = 200;

// Déclencheur en mode continu (voir core/burst.js) : après cette durée d'appui, une photo part tous les BURST_INTERVAL_MS tant qu'il est tenu
export const BURST_DELAY_MS = 500;
export const BURST_INTERVAL_MS = 450;

// Batterie du viseur : clignote quand il ne reste plus que ce nombre de photos
export const LOW_BATTERY_SHOTS = 3;

// Score d'une partie (#37, voir core/score.js) : le temps de jeu compte le plus, chaque nouveau kami un peu. Une batterie pleine
// (120 s) vaut 1 200 points, les 15 kamis d'une partie complète 750.
export const SCORE_POINTS_PER_SECOND = 10;
export const SCORE_POINTS_PER_KAMI = 50;

// Délai avant l'apparition du bouton d'action sur l'écran de fin de niveau
export const OVERLAY_BUTTON_DELAY_MS = 1500;

// Popup d'installation au lancement : proposée au plus une fois tous les N jours
export const INSTALL_OFFER_INTERVAL_DAYS = 60;

// Double-tap : ouvrir les réglages cachés (« 0cog™ » des mentions légales ou carte « Famille », voir core/doubleTap.js) ou remettre
// le son au maximum (bouton volume). Mesuré entre deux pointerup (socle UX de ZERO SLANG).
export const DOUBLE_TAP_MS = 350;

// Portrait plein cadre à la capture : durée d'affichage avant de s'effacer, indépendante du temps
// d'appui (voir ui/photoView.js) — une apparition furtive, pas un maintien.
export const PHOTO_REVEAL_MS = 750;

// Dérive des fantômes : vitesse de base (unités/s) au niveau 1, et gagnée à chaque niveau ;
// chaque membre de la famille applique son propre facteur (voir data/family.js)
export const GHOST_SPEED_GROWTH = 2;

// Aide « approche » (core/world.js#stepGhostToward / wanderNear) : si l'inactivité dépasse
// hintDelay × ce facteur, un fantôme vient à portée de vue au lieu d'attendre que le joueur le
// trouve, puis continue de flâner à l'écran (jamais figé, pas forcément dans le rayon de capture).
export const SUMMON_VICINITY = 110;    // rayon (unités monde) où il flâne ensuite, bien à l'écran
// Le kami appelé flâne en ligne droite et ne vire qu'une fois sorti de l'écran (voir wanderNear) : au-delà
// de la demi-diagonale du viseur plus la moitié d'un portrait, il est invisible, puis il revient en courbe.
export const SUMMON_RETURN_RADIUS = Math.round(Math.hypot(VIEW_SIZE / 2, VIEW_SIZE / 2) + GHOST_MAX_SIZE / 2);
export const SUMMON_HOVER_SPEED = 22;  // vitesse de flânerie une fois à portée de vue

// Message d'aide textuel (game.js#refreshHintMessage) : quelques secondes après que les flèches
// se mettent à pulser ou le réticule à clignoter (hintDelay), un message explique quoi faire —
// un seul à la fois, plusieurs indications actives défilent l'une après l'autre en boucle.
// Bulles d'aide textuelles (« Appuie sur le déclencheur », « Des kamis en haut : glisse… ») : mises de
// côté le temps de les revoir. Le code, les textes et le style sont conservés ; true les réactive.
// Le clignotement des coins du cadre et du réticule, lui, reste actif.
export const HINT_BUBBLES_ENABLED = false;
export const HINT_MESSAGE_DELAY_MS = 3000;  // ajouté à hintDelay avant le premier message
export const HINT_MESSAGE_HOLD_MS = 2600;   // durée d'affichage de chaque message avant le suivant

// Réglages dev par défaut (modifiables via le menu caché)
export const DEFAULT_DEV_SETTINGS = Object.freeze({
  worldGrowth: 320,    // Unités ajoutées au côté du monde par niveau (fantômes plus espacés)
  // Zone de déclenchement (où un tir cadre un kami) : le double du rayon du cercle télémétrique
  // affiché (vfCircle dans index.html, r=36) — plus tolérant que ce que le viseur montre, mais
  // loin de couvrir toute la fenêtre (VIEW_SIZE/2 = 160).
  captureRadius: 72,
  panSpeed: 120,       // Vitesse de l'objectif dès l'appui (unités/s)
  panMaxSpeed: 480,    // Vitesse atteinte si on reste appuyé
  panAccelTime: 1.2,   // Secondes pour passer de panSpeed à panMaxSpeed
  wheelSensitivity: 1, // Trackpad / molette : unités monde par pixel de défilement (à l'échelle du viseur)
  dragFriction: 4,     // Glisser : freinage de l'élan au lâcher (1/s) ; glissade ≈ vitesse / freinage
  gyroHorizonOffset: -5, // Gyroscope : inclinaison (degrés) du téléphone qui place l'horizon du décor au centre du viseur (le jeu démarre à -5°)
  gyroYawSpeed: 1,     // Gyroscope : vitesse de défilement horizontal (1 = un tour sur soi parcourt tout le monde)
  gyroPitchSpeed: 1,   // Gyroscope : vitesse de défilement vertical (1 = 60° d'inclinaison mènent au bord du monde)
  gyroSmoothing: 20,   // Gyroscope : lissage des mesures en % (0 = brut, plus haut = plus fluide mais plus lent)
  photoCostPercent: 5, // Une photo coûte ce pourcentage de la batterie (5 % = 20 photos par batterie pleine) ; le temps la vide aussi (batterySeconds)
  familySize: 0,       // Kamis dans la famille (types différents à trouver) : 0 = selon le niveau (LEVEL_FAMILY_SIZES), sinon 1..15 imposé
  summonDelay: 12,     // Un kami vient à toi après ce nombre de secondes d'inactivité (0 = tout de suite, en permanence : pour tester vite)
  summonSpeed: 90,     // Vitesse d'approche du kami qui vient à toi (unités monde/s), indépendante de la dérive du niveau
  ghostSpeed: 18,      // Vitesse de dérive des fantômes au niveau 1 (unités/s) ; 0 = immobiles
  hintDelay: 4,        // Secondes d'inactivité avant que les flèches n'indiquent où sont les fantômes
  musicBpm: 120,       // Tempo de la musique
  startLevel: 1,       // Niveau d'une nouvelle partie (après la fin du jeu, ou en « Relancer le jeu ») ; une batterie vide ne ramène pas ici
  batterySeconds: 120, // Autonomie de la batterie, en secondes de jeu, sans prendre de photo (chaque photo la vide aussi)
  chargeMinutes: 1,    // Batterie vide : durée de la charge avant de pouvoir recommencer (0 = recommencer tout de suite)
});

export const STORAGE_KEYS = Object.freeze({
  profile: 'zeroKami.profile',             // ancien profil (login retiré en version 1) : plus lu, purgé à la réinitialisation
  tipsSeen: 'zeroKami.tipsSeen',           // les astuces du premier lancement ont été vues : elles ne reviennent plus (réglages : « Revoir les astuces »)
  gallery: 'zeroKami.gallery',
  language: 'zeroKami.language',
  volume: 'zeroKami.volume',
  musicEnabled: 'zeroKami.musicEnabled',
  gyroEnabled: 'zeroKami.gyroEnabled',
  installOfferedAt: 'zeroKami.installOfferedAt',
  gameWon: 'zeroKami.gameWon',             // le dernier niveau est fini : on rejoue quand on veut, la galerie reste jusque-là
  level: 'zeroKami.level',                 // niveau en cours : on reprend la partie là où elle s'est arrêtée (après une charge, un rechargement)
  longLife: 'zeroKami.longLife',           // batterie longue durée en service (true) jusqu'à la prochaine batterie vide
  chargingUntil: 'zeroKami.chargingUntil', // instant (ms) où la batterie sera rechargée, après une batterie vide
  credits: 'zeroKami.credits',             // ancien solde de pièces (retiré en version 1) : plus lu, purgé à la réinitialisation
  iosFreeFastLevel: 'zeroKami.iosFreeFastLevel', // version iOS : niveau où la charge rapide offerte a été utilisée (une par niveau)
  shareRewards: 'zeroKami.shareRewards',   // récompenses du partage : kamis partagés, charges rapides et batteries longue durée gagnées
  bestScore: 'zeroKami.bestScore',         // meilleur score d'une partie sur cet appareil (#37)
  demoPlace: 'zeroKami.demoPlace',         // ancien lieu de démo (retiré en version 1) : plus lu, purgé à la réinitialisation
  settings: 'zeroKami.settings',
});
